"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { type NavItem, getLastId } from "../utils/NavItems";

import { Button } from "../../../ui/button";
import { DynamicIcon } from "../DynamicIcon";
import { IconSelector } from "./IconSelector";
import { Input } from "../../../ui/input";
import { LayoutList } from "lucide-react";
import { type NodeModel } from "@minoru/react-dnd-treeview";
import React from "react";
import { useForm } from "react-hook-form";
import { useSidebarItems } from "@/context/SideBarItemsProvider";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type CreateNavItemFormDialogProps = {
  parentId?: number;
  trigger?: React.ReactNode;
  navItem?: NodeModel<NavItem>;
};

export function CreateNavItemFormDialog(props: CreateNavItemFormDialogProps) {
  const { parentId, trigger, navItem } = props;
  const [open, setOpen] = React.useState(false);

  const handleSubmit = () => {
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ? (
          trigger
        ) : (
          <button className="flex">
            <div>
              <LayoutList size={16} className="mr-2" />
            </div>
            <div>New Item</div>
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{navItem ? "Edit shortcut" : "New shortcut"}</DialogTitle>
          <DialogDescription>
            A name, the address it opens, and an icon for its card on the dashboard.
          </DialogDescription>
        </DialogHeader>
        <CreateNavItemForm
          onSubmit={handleSubmit}
          onCancel={() => setOpen(false)}
          parentId={parentId}
          navItem={navItem}
        />
      </DialogContent>
    </Dialog>
  );
}

export function withScheme(link: string) {
  const trimmed = link.trim();
  if (!trimmed) return trimmed;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const navItemSchema = z.object({
  name: z.string().min(2).max(50),
  link: z.string().transform(withScheme).pipe(z.string().url("Enter a web address, like google.com")),
  icon: z.string(),
});

type CreateNavItemFormProps = {
  onCancel?: () => void;
  onSubmit?: () => void;
  parentId?: number;
  navItem?: NodeModel<NavItem>;
};

function CreateNavItemForm(props: CreateNavItemFormProps) {
  const { onSubmit, onCancel, parentId, navItem } = props;

  const { sidebarItems, setSidebarItems } = useSidebarItems();

  const form = useForm<z.infer<typeof navItemSchema>>({
    resolver: zodResolver(navItemSchema),
    defaultValues: {
      name: navItem?.text ?? "",
      link: navItem?.data?.href ?? "",
      icon: navItem?.data?.iconName,
    },
  });

  function handleSubmit(values: z.infer<typeof navItemSchema>) {
    if (navItem) {
      const updatedItems = sidebarItems.map((item) => {
        if (item.id === navItem.id) {
          return {
            ...item,
            text: values.name,
            data: {
              ...item.data,
              type: "external-link",
              href: values.link,
              iconName: values.icon,
              icon: (
                <DynamicIcon
                  name={values.icon}
                  size={24}
                  className="text-neutral-500"
                  title={values.icon}
                />
              ),
            },
          };
        }
        return item;
      });
      setSidebarItems(updatedItems);
    } else {
      const lastId = ((getLastId(sidebarItems) as number) ?? 0) + 1;
      setSidebarItems([
        ...sidebarItems,
        {
          text: values.name,
          parent: parentId ?? 0,
          droppable: false,
          id: lastId,
          data: {
            type: "external-link",
            href: values.link,
            iconName: values.icon,
            icon: (
              <DynamicIcon
                name={values.icon}
                size={24}
                className="text-neutral-500"
                title={values.icon}
              />
            ),
          },
        },
      ]);
    }

    onSubmit?.();
  }

  const preview = form.watch();

  return (
    <Form {...form}>
      <form
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        className="space-y-5"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-24 w-28 shrink-0 flex-col items-center justify-center gap-2 rounded-xl border bg-card text-center shadow-sm" data-testid="shortcut-preview">
            <span className="text-neutral-500">
              {preview.icon ? <DynamicIcon name={preview.icon} size={24} /> : <LayoutList size={24} className="text-muted-foreground/50" />}
            </span>
            <span className="max-w-[6.5rem] truncate px-2 text-xs font-semibold">{preview.name || "Shortcut"}</span>
          </div>
          <div className="flex-1 space-y-3">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Helios Orange" className="w-full" data-testid="shortcut-name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="helios.example.com"
                      className="w-full"
                      data-testid="shortcut-address"
                      {...field}
                      onBlur={() => { field.onChange(withScheme(field.value)); field.onBlur(); }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <FormField
          control={form.control}
          name="icon"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Icon</FormLabel>
              <FormControl>
                <IconSelector onSelectIcon={field.onChange} value={field.value} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
          <Button type="submit" data-testid="shortcut-save">{navItem ? "Save changes" : "Save"}</Button>
        </div>
      </form>
    </Form>
  );
}
